import { AlertCircle, ArrowDownRight, ArrowUpRight, Inbox, LoaderCircle, RefreshCw } from 'lucide-react';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

/** Avatar letters: first letter of the first two real words ("Junk King" → "JK"). One rule everywhere.
 *  Words that start with a symbol ("&", "(temporary,") are skipped, and a bracketed note is
 *  ignored, so "Winton & Hiestand" → "WH" and "Claude (temporary, Windsor linking)" → "C". */
export const monogram = (name: string | null | undefined) => {
  const startsReal = (w: string) => /^[\p{L}\p{N}]/u.test(w);
  const words = (raw: string) => raw.split(/\s+/).filter(startsReal);
  const text = String(name ?? "");
  const picked = words(text.replace(/\([^)]*\)?/g, " "));
  return (picked.length ? picked : words(text))
    .slice(0, 2)
    .map((w) => Array.from(w)[0])
    .join("")
    .toUpperCase();
};

// `eyebrow` is accepted for existing callers but not drawn: the breadcrumb
// already locates the page, so a kicker above the title said it a third time.
export function PageHeader({ title, description, actions }: {
  eyebrow?: string;
  title: string;
  description?: string;
  actions?: ReactNode;
}) {
  return (
    <div className="page-header">
      <div>
        <h1>{title}</h1>
        {description && <p className="page-description">{description}</p>}
      </div>
      {actions && <div className="page-actions">{actions}</div>}
    </div>
  );
}

export function MetricCard({ label, value, detail, change, icon, to, tone }: {
  label: string;
  value: ReactNode;
  detail?: string;
  change?: number | null;
  icon?: ReactNode;
  to?: string;
  /** Overrides the colour when "up" is not good news (CPA, CPC) or not news at all (spend). */
  tone?: 'positive' | 'negative' | 'neutral' | null;
}) {
  const positive = typeof change === 'number' && change >= 0;
  const toneClass = tone ?? (positive ? 'positive' : 'negative');
  const content = (
    <>
      <div className="metric-top"><span>{label}</span></div>
      <strong className="metric-value">{value}</strong>
      <div className="metric-detail">
        {typeof change === 'number' && (
          // No change is no arrow (it used to read "↗0.0%").
          <span className={`trend ${change === 0 ? 'neutral' : toneClass}`}>
            {change === 0 ? null : positive ? <ArrowUpRight aria-hidden="true" /> : <ArrowDownRight aria-hidden="true" />}
            {Math.abs(change).toFixed(1)}%
          </span>
        )}
        {detail && <span>{detail}</span>}
      </div>
    </>
  );
  return to ? <Link className="metric-card metric-card-link" to={to} aria-label={`Open ${label}`}>{content}</Link> : <article className="metric-card">{content}</article>;
}

export function StatusBadge({ children, tone = 'neutral' }: {
  children: ReactNode;
  tone?: 'neutral' | 'success' | 'warning' | 'danger' | 'info';
}) {
  // Plain text gets a capital first letter; everything else reads as written.
  const text = typeof children === 'string' && children ? children.charAt(0).toUpperCase() + children.slice(1) : children;
  return <span className={`status-badge ${tone}`}>{text}</span>;
}

/**
 * A health score's colour is its retention risk. The risk is computed against the
 * configured cutoffs (Settings, default 70/50); a fixed 75/55 here coloured a 76
 * green beside a "medium risk" badge. Without a risk, the default cutoffs apply.
 * `stale` greys a score nobody is recalculating (a paused or prospect client).
 */
export function Score({ value, label, risk, stale }: { value: number | null | undefined; label?: string; risk?: 'low' | 'medium' | 'high' | null; stale?: boolean }) {
  if (value === null || value === undefined) return <span className="muted">Not scored</span>;
  const tone = stale ? 'stale' : risk ? ({ low: 'good', medium: 'watch', high: 'risk' } as const)[risk] : value >= 70 ? 'good' : value >= 50 ? 'watch' : 'risk';
  return (
    <span className={`score ${tone}`} aria-label={`${label || 'Score'} ${value} out of 100`}>
      <span>{value}</span><small>/100</small>
    </span>
  );
}

export function LoadingState({ label = 'Loading workspace' }: { label?: string }) {
  return <div className="state-panel" role="status"><LoaderCircle className="spin" aria-hidden="true" /><strong>{label}</strong></div>;
}

export function PageSkeleton({ cards = 4 }: { cards?: number }) {
  return (
    <div className="page-skeleton" role="status" aria-label="Loading workspace">
      <span className="sr-only">Loading workspace</span>
      <div className="skeleton-heading"><i /><i /></div>
      <div className="skeleton-cards">{Array.from({ length: cards }, (_, index) => <div className="skeleton-card" key={index}><i /><i /><i /></div>)}</div>
      <div className="skeleton-panel">{Array.from({ length: 5 }, (_, index) => <i key={index} />)}</div>
    </div>
  );
}

export function ErrorState({ message, onRetry, title = 'Couldn’t load this view' }: { message: string; onRetry?: () => void; title?: string }) {
  return (
    <div className="state-panel error-panel" role="alert">
      <AlertCircle aria-hidden="true" />
      <div><strong>{title}</strong><p>{message}</p></div>
      {onRetry && <button className="button secondary" onClick={onRetry}><RefreshCw aria-hidden="true" />Retry</button>}
    </div>
  );
}

export function EmptyState({ title, description, icon, action }: { title: string; description: string; icon?: ReactNode; action?: ReactNode }) {
  return <div className="empty-state"><span className="empty-state-icon">{icon || <Inbox aria-hidden="true" />}</span><strong>{title}</strong><p>{description}</p>{action && <div className="empty-state-action">{action}</div>}</div>;
}
